import { renderToString } from "jsx-async-runtime";
import Money from "../model/money";
import PaymentSettings from "../model/payment/PaymentSettings";
import { PAYMENTS_SUMMARY } from "../constants";

export class PaymentsSummary extends HTMLElement {
  private _formSettings: PaymentSettings;

  constructor() {
    super();
  }

  get formSettings(): PaymentSettings {
    return this._formSettings;
  }

  set formSettings(value: PaymentSettings) {
    this._formSettings = value;
  }

  formatMoney(value: Money) {
    const formSettingsData = this.formSettings;

    if (!value) return "";

    if (value.value === 0)
      return formSettingsData.resources.commonPayment.freeProductText;

    let cultureCode = formSettingsData.cultureCode;

    if (cultureCode === "ar-SA") cultureCode = "ar-u-nu-latn";

    return Intl.NumberFormat(cultureCode, {
      style: "currency",
      currency: value.currency,
      currencyDisplay: "symbol",
    }).format(value.value);
  }

  async connectedCallback() {
    const formSettingsData = this.formSettings;

    const additionalClass =
      formSettingsData.paymentSummary.membershipType === "Plus" ? "plus" : "";

    const html = (
      <div class={`payment-summary ${additionalClass}`}>
        {!!formSettingsData.paymentSummary.gymName &&
        !!formSettingsData.paymentSummary.purchaseDescription ? (
          <div
            data-testid="summaryHeaderSection"
            class="payment-summary-header-section"
          >
            <div
              data-testid="membershipHeader"
              class={`membership-header ${additionalClass}`}
            >
              <div data-testid="productDescription" class="product-description">
                <div class="wrapper">
                  <p class={`text ${additionalClass}`}>
                    {formSettingsData.paymentSummary.purchaseDescription}
                  </p>
                </div>
              </div>
              <div data-testid="gymName" class="gym-name">
                <div class="wrapper">
                  <p class="text">{formSettingsData.paymentSummary.gymName}</p>
                </div>
              </div>
            </div>
            <div
              data-testid="additionalInfo"
              class={`additional-info ${additionalClass}`}
            >
              {formSettingsData.paymentSummary.multiGymCount ? (
                <div data-testid="multiGymCount" class="additional-info-entry">
                  <div class="wrapper">
                    <p class="text">
                      {formSettingsData.resources.paymentSummary.numberOfGyms}
                    </p>
                  </div>
                  <p class="text">
                    {formSettingsData.paymentSummary.multiGymCount}
                  </p>
                </div>
              ) : (
                ""
              )}
              {formSettingsData.paymentSummary.startDate ? (
                <div data-testid="startDate" class="additional-info-entry">
                  <div class="wrapper">
                    <p class="text">
                      {formSettingsData.resources.paymentSummary.startDate}
                    </p>
                  </div>
                  <p class="text">
                    {formSettingsData.paymentSummary.startDate}
                  </p>
                </div>
              ) : (
                ""
              )}
              {formSettingsData.paymentSummary.endDate ? (
                <div data-testid="endDate" class="additional-info-entry">
                  <div class="wrapper">
                    <p class="text">
                      {formSettingsData.resources.paymentSummary.endDate}
                    </p>
                  </div>
                  <p class="text">{formSettingsData.paymentSummary.endDate}</p>
                </div>
              ) : (
                ""
              )}
            </div>
          </div>
        ) : (
          ""
        )}
        <div class={`summary-wrapper ${additionalClass}`}>
          <div
            data-testid="paymentTimelineSummary"
            class="payment-timeline-summary"
          >
            {formSettingsData.paymentTimelineSummary.overviewLines.map(
              (overviewLine, idx) => {
                let additionalClass =
                  document.documentElement.dir === "rtl" ? "rtl" : "";
                if (idx === 0) {
                  if (formSettingsData.paymentSummary.membershipType === "Plus")
                    additionalClass = additionalClass.concat(" plus");
                  else additionalClass = additionalClass.concat(" core");
                } else {
                  additionalClass = additionalClass.concat(" next-payments");
                }
                return (
                  <div
                    dir={document.documentElement.dir}
                    class={`timeline-entry ${additionalClass}`}
                  >
                    <div class="dot-connector-wrapper">
                      <span
                        data-testid="timelineDot-1"
                        class={`dot ${additionalClass}`}
                      ></span>
                      <span
                        data-testid="timelineConnector"
                        class={
                          formSettingsData.paymentTimelineSummary.overviewLines
                            .length !==
                          idx + 1
                            ? "connector"
                            : ""
                        }
                      ></span>
                    </div>
                    <div
                      data-testid="initialPaymentSummaryCard"
                      class={`card ${additionalClass}`}
                    >
                      <div class="wrapper">
                        <div
                          data-testid="initialPaymentSummaryCardOverviewLine"
                          class="overview-line"
                        >
                          <div class="wrapper">
                            <div class="overview-summary-description-wrapper">
                              <p
                                data-testid="initialPaymentSummaryOverviewDescriptionSummary"
                                class="overview-summary-description"
                              >
                                {overviewLine.description}
                              </p>
                            </div>
                            <p
                              data-testid="initialPaymentSummaryOverviewNormalAmountSummary"
                              class="overview-normal-amount"
                            >
                              {this.formatMoney(overviewLine.normalAmount)}
                            </p>
                            <p
                              data-testid="initialPaymentSummaryOverviewAmountSummary"
                              class="overview-amount"
                            >
                              {this.formatMoney(overviewLine.amount)}
                            </p>
                          </div>
                        </div>
                        {overviewLine.orderLines.map((orderLine, idx) => {
                          return (
                            <div
                              data-testid={`${idx}-initialPaymentOrderLine`}
                              class="order-line"
                            >
                              <div class="wrapper">
                                <div class="order-line-description-wrapper">
                                  <p
                                    data-testid={`${idx}-initialPaymentOrderLineDescription`}
                                    class="order-line-description"
                                  >
                                    {orderLine.description}
                                  </p>
                                </div>
                                <p
                                  data-testid={`${idx}-initialPaymentOrderLineNormalAmount`}
                                  class="order-line-normal-amount"
                                >
                                  {this.formatMoney(orderLine.normalAmount)}
                                </p>
                                <p
                                  data-testid={`${idx}-initialPaymentOrderLineAmount`}
                                  class="order-line-amount"
                                >
                                  {this.formatMoney(orderLine.amount)}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              }
            )}
          </div>
          <div data-testid="finePrintSection" class="fine-print-section">
            <ul variant="standard" class="fine-print-list">
              {formSettingsData.paymentTimelineSummary.finePrintLines
                .filter((l) => l.type === "Subscription")
                .map((fineprintLine, idx) => {
                  return (
                    <li
                      data-testid={`finePrintLine${idx}`}
                      class="fine-print-entry"
                    >
                      {fineprintLine.info}
                    </li>
                  );
                })}
            </ul>
          </div>
        </div>
      </div>
    );

    this.innerHTML = await renderToString(html);
  }
}

if (!customElements.get(PAYMENTS_SUMMARY)) {
  customElements.define(PAYMENTS_SUMMARY, PaymentsSummary);
}
